import { KeyboardArrowDown } from "@mui/icons-material";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import CloseIcon from "@mui/icons-material/CloseRounded";
import {
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tooltip,
  ClickAwayListener,
} from "@mui/material";
import cx from "classnames";
import { multiply, uniqBy } from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { ReactComponent as CrownIcon } from "assets/crown.svg";
import { ReactComponent as ExportIcon } from "assets/export-icon.svg";
import { ReactComponent as Close } from "assets/x-mark.svg";
import ShotContext from "components/Shot/ShotContext";
import ThrottlePopup from "components/ThrottlePopup";
import {
  DEPTH_MAP_EXPORT_MODES,
  EXPORT_MODES,
  PLANS,
  RETIME_EXPORT_MODES,
  SERVICES,
  SHOT_SOURCES,
  SMART_VECTORS_EXPORT_MODES,
  TRACKING_EXPORT_MODES,
} from "constants/constants";
import {
  SLAPSHOT_MY_PLAN_UPGRADE_URL,
  SLAPSHOT_SUBSCRIPTION_UPGRADE_URL,
} from "constants/urls";
import { UserContext } from "layouts/SlapshotMainLayout";
import { LayerCompact } from "models/Layer";
import BulkExport, {
  ExportMessage,
  ExportWarningDialog,
} from "pages/BulkExport";
import { createExport } from "services/ExportService";
import { isShotBillable } from "utils/helper";
import { useCountdown } from "utils/hooks";

import styles from "./Shot.module.scss";

const ExportPanel = () => {
  const user = useContext(UserContext);
  const {
    isExporting,
    setIsExporting,
    project,
    setShowExportPanel,
    layersForExport,
    setLayersForExport,
    service,
    selectedShot,
    subscription,
    setLayers,
    throttleUsage,
    getThrottleUsage,
  } = useContext(ShotContext);
  const navigate = useNavigate();

  const [showExportMessage, setShowExportMessage] = useState(false);
  const [showExportQuotaWarning, setShowExportQuotaWarning] = useState(false);
  const [trackingLayersForExport, setTrackingLayersForExport] = useState<
    Record<string, LayerCompact[]>
  >({});
  const [selectedGroupsForExport, setSelectedGroupsForExport] = useState<
    string[]
  >([]);
  const [shouldUpgradeForExport, setShouldUpgradeForExport] = useState("");
  const [requiredFrames, setrequiredFrames] = useState(0);
  const [requiredAutopilotFrames, setrequiredAutopilotFrames] = useState(0);
  const [showThrottleDialog, setShowThrottleDialog] = useState(false);

  const layersToExport = useMemo(() => {
    if (service === SERVICES.TRACKING) {
      return selectedGroupsForExport.flatMap(
        (key) => trackingLayersForExport[key]?.map((layer) => layer.id) || [],
      );
    }
    return layersForExport.map((layer) => layer.id);
  }, [
    service,
    layersForExport,
    selectedGroupsForExport,
    trackingLayersForExport,
  ]);

  const throttledTill = useMemo(() => {
    if (
      throttleUsage?.is_export_throttled &&
      (throttleUsage.allowed_exports || 0) < 1 &&
      throttleUsage.next_export_provision_at
    ) {
      return new Date(throttleUsage.next_export_provision_at);
    }
    return null;
  }, [throttleUsage]);
  const countdown = useCountdown(throttledTill, getThrottleUsage);

  const SERVICE_EXPORT_MODES = (() => {
    switch (service) {
      case SERVICES.DEPTH_MAP:
        return DEPTH_MAP_EXPORT_MODES;
      case SERVICES.SMART_VECTORS:
        return SMART_VECTORS_EXPORT_MODES;
      case SERVICES.RETIME:
        return RETIME_EXPORT_MODES;
      case SERVICES.TRACKING:
        return TRACKING_EXPORT_MODES;
      default:
        return EXPORT_MODES;
    }
  })();

  const DEFAULT_EXPORT_MODE = (() => {
    switch (service) {
      case SERVICES.DEPTH_MAP:
        return DEPTH_MAP_EXPORT_MODES.JPG_MODE;
      case SERVICES.SMART_VECTORS:
        return SMART_VECTORS_EXPORT_MODES.EXR_MODE;
      case SERVICES.RETIME:
        return RETIME_EXPORT_MODES.MOV_MODE;
      case SERVICES.TRACKING:
        return TRACKING_EXPORT_MODES.ALL_MODE;
      default:
        return EXPORT_MODES.JPG_MODE;
    }
  })();

  const exportModes = useMemo(() => {
    const modes =
      user?.preferences?.exportModes && user?.preferences?.exportModes[service];
    return modes?.length ? modes : [DEFAULT_EXPORT_MODE];
  }, [user?.preferences?.exportModes, service, DEFAULT_EXPORT_MODE]);

  const changeExportMode = useCallback(
    (
      newMode:
        | EXPORT_MODES
        | DEPTH_MAP_EXPORT_MODES
        | SMART_VECTORS_EXPORT_MODES,
    ) => {
      user?.setPreferences(
        "exportModes",
        {
          ...user.preferences?.exportModes,
          [service]: [newMode],
        },
        false,
      );
    },
    [user, service],
  );

  const isExportDisabled = useMemo(() => {
    const noLayersForExport =
      service !== SERVICES.TRACKING
        ? layersForExport.length === 0
        : selectedGroupsForExport.length === 0;
    return isExporting || noLayersForExport || !!throttledTill;
  }, [
    service,
    layersForExport.length,
    selectedGroupsForExport.length,
    isExporting,
    throttledTill,
  ]);

  const onExport = useCallback(
    async (layersToBeExported: number[]) => {
      setIsExporting(true);
      const { id } = project;
      createExport(+id, layersToBeExported, exportModes)
        .then(() => {
          setLayers((prev) => {
            return prev?.map((layer) => {
              if (layersToBeExported.includes(layer.id)) {
                return { ...layer, export_count: layer.export_count + 1 };
              }
              return layer;
            });
          });
          setShowExportMessage(true);
          getThrottleUsage();
        })
        .catch((err) => {
          if (err.detail) toast.error(err.detail);
          else toast.error("Something went wrong");
        })
        .finally(() => {
          setLayersForExport([]);
          setIsExporting(false);
        });
    },
    [
      exportModes,
      getThrottleUsage,
      project,
      setIsExporting,
      setLayers,
      setLayersForExport,
    ],
  );

  const onBeforeExport = useCallback(
    (layerToBeExported) => {
      if (!subscription) return;
      if (
        ((requiredFrames > 0 &&
          subscription.frames_used + requiredFrames >
            subscription.frames_limit) ||
          (requiredAutopilotFrames > 0 &&
            subscription.autopilot_frames_used + requiredAutopilotFrames >
              subscription.autopilot_frames_limit)) &&
        !user?.preferences?.hideFramesQuotaWarning
      ) {
        setShowExportQuotaWarning(true);
      } else {
        onExport(layerToBeExported);
      }
    },
    [
      onExport,
      requiredAutopilotFrames,
      requiredFrames,
      subscription,
      user?.preferences?.hideFramesQuotaWarning,
    ],
  );

  useEffect(() => {
    if (!subscription || !selectedShot || subscription.is_free_trial_period)
      return;
    let shouldUpgrade = "";

    // resolution check
    if (
      isShotBillable(selectedShot) &&
      selectedShot.resolution >
        subscription.plan.resolution.dimension.reduce(multiply)
    )
      shouldUpgrade = `Max resolution allowed for your plan is ${subscription.plan.resolution.dimension.join("x")}`;

    let framesRequired = 0;
    let autopilotFramesRequired = 0;

    if ([SERVICES.TRACKING].includes(service)) {
      Object.values(trackingLayersForExport).forEach((layers) => {
        const autopilotLayers = layers.filter(
          (layer) => layer.is_autopilot_layer,
        );
        const nonAutopilotLayers = layers.filter(
          (layer) => !layer.is_autopilot_layer,
        );

        if (isShotBillable(selectedShot)) {
          autopilotLayers.forEach((layer) => {
            if (layer.export_count === 0) {
              autopilotFramesRequired += selectedShot.frame_count;
            }
          });
        }

        // For tracking, we don't consider the keyframes towards quota
        if (
          isShotBillable(selectedShot) &&
          !nonAutopilotLayers.some((layer) => layer.export_count > 0)
        ) {
          framesRequired += selectedShot.frame_count;
        }
      });
    } else {
      layersForExport.forEach((layer) => {
        if (isShotBillable(selectedShot) && layer.export_count === 0) {
          if (layer.is_autopilot_layer)
            autopilotFramesRequired += selectedShot.frame_count;
          else framesRequired += selectedShot.frame_count;
        }
      });
    }

    // export frames check for free plan
    if (
      isShotBillable(selectedShot) &&
      framesRequired > 0 &&
      !subscription.plan.extra_frame_cost.unit_amount_decimal &&
      subscription.frames_used + framesRequired > subscription.frames_limit
    ) {
      shouldUpgrade = `Max frames allowed for your plan is ${subscription.frames_limit}`;
    }

    // autopilot export frames check
    if (
      isShotBillable(selectedShot) &&
      autopilotFramesRequired > 0 &&
      !subscription.plan.autopilot_frame_cost.unit_amount_decimal &&
      subscription.autopilot_frames_used + autopilotFramesRequired >
        subscription.autopilot_frames_limit
    ) {
      shouldUpgrade = `Max Autopilot frames allowed for your plan is ${subscription.autopilot_frames_limit}`;
    }

    setrequiredFrames(framesRequired);
    setrequiredAutopilotFrames(autopilotFramesRequired);
    setShouldUpgradeForExport(shouldUpgrade);
  }, [
    service,
    layersForExport,
    subscription,
    selectedShot,
    trackingLayersForExport,
  ]);

  useEffect(() => {
    if (service === SERVICES.TRACKING) {
      const grouped: Record<string, LayerCompact[]> = {};
      layersForExport.forEach((layer, index) => {
        const key =
          layer.inference_id === null
            ? `null-${index}`
            : `inference-${layer.inference_id}`;
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(layer);
      });
      setTrackingLayersForExport(grouped);
      setSelectedGroupsForExport(Object.keys(grouped));
    }
  }, [service, layersForExport]);

  return (
    <>
      <div className={styles.exportContainer}>
        <div className={styles.shotsHeader}>
          <h5>Export</h5>
          <button
            type="button"
            className={styles.closeExportButton}
            onClick={() => {
              setShowExportPanel(false);
            }}
          >
            <Close />
          </button>
        </div>

        <div className={styles.shotsForExport}>
          {service !== SERVICES.TRACKING
            ? layersForExport.map((l) => (
                <div key={l.id} className={styles.shotExport}>
                  <div className={styles.layerExportDetail}>
                    {l.cover_image_url ? (
                      <img
                        alt={l.cover_image_url}
                        className={styles.exportThumbnail}
                        src={l.cover_image_url}
                      />
                    ) : (
                      <AspectRatioIcon className={styles.exportThumbnail} />
                    )}
                    <p>{l.name}</p>
                  </div>
                  <CloseIcon
                    className={styles.removeShot}
                    onClick={() => {
                      setLayersForExport((exportLayers) =>
                        exportLayers.filter((layer) => layer.id !== l.id),
                      );
                    }}
                  />
                </div>
              ))
            : selectedGroupsForExport.map((layerGroupKey) => {
                const layerGroup = trackingLayersForExport[layerGroupKey];
                const l = layerGroup[0];

                return (
                  <div key={l.id} className={styles.shotExport}>
                    <div className={styles.layerExportDetail}>
                      {l.cover_image_url ? (
                        <img
                          alt={l.cover_image_url}
                          className={styles.exportThumbnail}
                          src={l.cover_image_url}
                        />
                      ) : (
                        <AspectRatioIcon className={styles.exportThumbnail} />
                      )}

                      <p>
                        Keyframe:{" "}
                        <span>
                          {layerGroup
                            .map((layer) => layer.frame_number)
                            .sort((a, b) => a - b)
                            .join(", ")}
                        </span>
                      </p>
                    </div>

                    <CloseIcon
                      className={styles.removeShot}
                      onClick={() => {
                        setSelectedGroupsForExport((prev) =>
                          prev.filter((k) => k !== layerGroupKey),
                        );
                      }}
                    />
                  </div>
                );
              })}
        </div>

        <div className={styles.exportFooter}>
          {![
            SERVICES.SMART_VECTORS,
            SERVICES.RETIME,
            SERVICES.TRACKING,
          ].includes(service) && (
            <div className={styles.exportOptions}>
              <div className={styles.viewTabs}>
                {Object.entries(SERVICE_EXPORT_MODES).map(([key, value]) => (
                  <Button
                    key={key}
                    onClick={() => changeExportMode(value)}
                    size="small"
                    className={cx(
                      styles.viewTab,
                      exportModes.includes(value) && styles.active,
                    )}
                    style={{
                      width: "50%",
                    }}
                  >
                    {value}
                  </Button>
                ))}
              </div>
              <p>
                All the layers inside the shot will be exported as a package
              </p>
            </div>
          )}

          {subscription &&
            selectedShot &&
            (shouldUpgradeForExport ? (
              <div className={styles.upgradeBtncon}>
                <Tooltip
                  title={shouldUpgradeForExport}
                  placement="top"
                  arrow
                  slotProps={{
                    tooltip: {
                      sx: {
                        bgcolor: "#2b2b2b",
                        maxWidth: "400px",
                        borderRadius: "8px",
                        "& .MuiTooltip-arrow": {
                          color: "#2b2b2b",
                        },
                        padding: "8px",
                      },
                    },
                  }}
                >
                  <Button
                    disabled={!project.company.is_admin}
                    className={styles.upgrade}
                    onClick={() =>
                      navigate(
                        project.company.is_owner
                          ? SLAPSHOT_MY_PLAN_UPGRADE_URL
                          : `${SLAPSHOT_SUBSCRIPTION_UPGRADE_URL}?workspaceId=${project.company.id}&workspaceName=${project.company.name}`,
                      )
                    }
                  >
                    <CrownIcon className={styles.crownIcon} />
                    Upgrade to export
                  </Button>
                </Tooltip>
              </div>
            ) : (
              <Button
                disabled={isExportDisabled}
                onClick={() => {
                  if (
                    throttleUsage?.is_export_throttled &&
                    (throttleUsage?.allowed_exports || 0) <
                      layersToExport.length
                  ) {
                    setShowThrottleDialog(true);
                  } else {
                    onBeforeExport(layersToExport);
                  }
                }}
                className={styles.primaryButton}
              >
                {countdown ? `Export in ${countdown}` : "Export"}
              </Button>
            ))}
        </div>
      </div>

      {showThrottleDialog && (
        <ThrottlePopup
          layers={
            [SERVICES.TRACKING].includes(service)
              ? selectedGroupsForExport.map(
                  (key) => trackingLayersForExport[key][0],
                )
              : layersForExport
          }
          context="Export"
          onClose={() => setShowThrottleDialog(false)}
          onSubmit={(layerIds) => {
            setShowThrottleDialog(false);
            let layersToBeExported: number[] = [];
            if ([SERVICES.TRACKING].includes(service)) {
              layersToBeExported = selectedGroupsForExport
                .filter((key) =>
                  layerIds.includes(trackingLayersForExport[key][0].id),
                )
                .flatMap((key) =>
                  trackingLayersForExport[key].map((l) => l.id),
                );
            } else {
              layersToBeExported = layersForExport
                .filter((layer) => layerIds.includes(layer.id))
                .map((l) => l.id);
            }
            onBeforeExport(layersToBeExported);
          }}
        />
      )}

      {showExportMessage && (
        <ExportMessage
          onClose={() => {
            setShowExportMessage(false);
            setShowExportPanel(false);
          }}
        />
      )}

      {showExportQuotaWarning && (
        <ExportWarningDialog
          open={showExportQuotaWarning}
          onClose={() => setShowExportQuotaWarning(false)}
          onContinue={() => {
            setShowExportQuotaWarning(false);
            onExport(layersToExport);
          }}
          requiredFrames={requiredFrames}
          requiredAutopilotFrames={requiredAutopilotFrames}
        />
      )}
    </>
  );
};

export default ExportPanel;

export const ExportButton = () => {
  const { setShowExportPanel, throttleUsage, getThrottleUsage } =
    useContext(ShotContext);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showBulkExportModal, setShowBulkExportModal] = useState(false);

  const BULK = "BULK";
  const SINGLE_SHOT = "SINGLE_SHOT";
  const [exportType, setExportType] = useState(SINGLE_SHOT);

  const throttledTill = useMemo(() => {
    if (
      throttleUsage?.is_export_throttled &&
      (throttleUsage.allowed_exports || 0) < 1 &&
      throttleUsage.next_export_provision_at
    ) {
      return new Date(throttleUsage.next_export_provision_at);
    }
    return null;
  }, [throttleUsage]);
  const countdown = useCountdown(throttledTill, getThrottleUsage);

  return (
    <>
      <Tooltip
        arrow
        placement="bottom-end"
        slotProps={{
          tooltip: {
            sx: {
              padding: "0",
              bgcolor: "transparent",
              "& .MuiTooltip-arrow": {
                color: "#2C2C2C",
                left: "33px !important",
              },
            },
          },
        }}
        open={showExportModal}
        title={
          <ClickAwayListener
            onClickAway={() => {
              setShowExportModal(false);
            }}
          >
            <div className={styles.emContainer}>
              <div className={styles.emLabel}>Export:</div>

              <RadioGroup
                aria-label="service"
                name="service"
                value={exportType}
                onChange={(e) => setExportType(e.target.value)}
              >
                {Array.from([SINGLE_SHOT, BULK]).map((value) => (
                  <FormControlLabel
                    key={value}
                    value={value}
                    control={
                      <Radio
                        sx={{
                          color: "#CCCCDCA6",
                          "&.Mui-checked": {
                            color: "#6565EC",
                          },
                          "& .MuiSvgIcon-root": {
                            fontSize: "14px",
                          },
                        }}
                      />
                    }
                    label={value === BULK ? "In bulk" : "Selected shot only"}
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        color: "#CCCCDC",
                        fontSize: "14px",
                        fontFamily: "Inter",
                      },
                      "& .Mui-checked + .MuiFormControlLabel-label": {
                        color: "#FFFFFFE5",
                        fontWeight: 500,
                      },
                    }}
                  />
                ))}
              </RadioGroup>

              <Button
                className={cx(styles.outlineButton, styles.emBtn)}
                onClick={() => {
                  setShowExportModal(false);
                  if (exportType === BULK) {
                    setShowBulkExportModal(true);
                  } else {
                    setShowExportPanel(true);
                  }
                }}
              >
                Next
              </Button>
            </div>
          </ClickAwayListener>
        }
      >
        <Button
          disabled={!!throttledTill}
          className={styles.exportButton}
          variant="contained"
          onClick={() => {
            setShowExportModal(true);
          }}
          color="secondary"
          size="small"
          sx={{
            ".MuiSvgIcon-root": {
              width: "14px",
              height: "14px",
            },
          }}
        >
          <ExportIcon style={{ marginRight: "8px" }} />
          {countdown ? `Export in ${countdown}` : "Export"}
          <KeyboardArrowDown className={styles.arrowDownIcon} />
        </Button>
      </Tooltip>

      {showBulkExportModal && (
        <BulkExport setShowBulkExportModal={setShowBulkExportModal} />
      )}
    </>
  );
};
